/**
 * RoleGuard — the one client-side gate. Two ways to use it:
 *
 *   // tier gate (what every /admin/* route gets via RequireAuth)
 *   <RoleGuard minTier="manager"><BankDashboard /></RoleGuard>
 *
 *   // explicit allow-list (portals, single features)
 *   <RoleGuard allow={{ userTypes: ['teacher'] }}><TeacherPortal /></RoleGuard>
 *   <RoleGuard allow={{ permissions: ['security.gate_scan'] }}><GatePage /></RoleGuard>
 *
 * A tier gate renders a clear "you need X" panel and stays put, so the user
 * can read why. An allow-list gate sends them to their own workspace via
 * /api/auth/landing.
 *
 * NOTE: client-side convenience only — the backend still enforces every
 * endpoint with the real permission dependency.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { authHeaders } from '@/lib/authToken';
import { Button } from '@/components/ui/button';
import { canonicalRole, rankOf, meetsTier } from '@/lib/roleTier';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = {
  super_admin: 'Super Admin',
  platform_admin: 'Platform Admin',
  org_admin: 'Organisation Admin',
  manager: 'Manager',
  operator: 'Operator',
  viewer: 'Viewer',
};

export function userAllowed(user, allow) {
  if (!user) return false;
  if (user.is_super_admin === true || user.role === 'super_admin') return true;
  if (Array.isArray(user.permissions) && user.permissions.includes('*')) return true;

  if (allow?.userTypes?.length) {
    const ut = (user.user_type || user.type || '').toLowerCase();
    if (allow.userTypes.map((x) => x.toLowerCase()).includes(ut)) return true;
  }
  if (allow?.roles?.length) {
    const r = (user.role || '').toLowerCase();
    if (allow.roles.map((x) => x.toLowerCase()).includes(r)) return true;
  }
  if (allow?.permissions?.length) {
    const perms = new Set(user.permissions || []);
    if (allow.permissions.some((p) => perms.has(p))) return true;
  }
  return false;
}

const TierNotice = ({ minTier, user }) => (
  <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="role-guard-notice">
    <div className="max-w-md text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
        <Lock className="h-5 w-5 text-amber-700" />
      </div>
      <h1 className="text-xl font-bold text-slate-900">
        You need {TIER_LABELS[minTier] || minTier} access
      </h1>
      <p className="text-sm text-slate-600 mt-2">
        This section is limited to {TIER_LABELS[minTier] || minTier} and above. Your role
        {user?.role ? ` (${user.role})` : ''} sits below that, so the data would not load
        even if the page opened. Ask a super admin to raise your role or to scope you to the
        right unit.
      </p>
      <Link to="/admin" className="inline-block mt-5">
        <Button variant="outline" size="sm" data-testid="role-guard-back">
          Back to dashboard
        </Button>
      </Link>
    </div>
  </div>
);

export default function RoleGuard({ allow, minTier, children, fallback = null }) {
  const { user, token } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  const tierBlocked = Boolean(minTier) && user && !meetsTier(user, minTier);

  useEffect(() => {
    if (minTier) return;                       // tier gates render a notice, no redirect
    let cancelled = false;
    (async () => {
      if (!token) {
        const back = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?redirect=${back}`, { replace: true });
        return;
      }
      if (userAllowed(user, allow)) {
        if (!cancelled) setChecked(true);
        return;
      }
      try {
        const r = await fetch(`${API_URL}/api/auth/landing`, { headers: authHeaders() });
        const data = r.ok ? await r.json() : { url: '/' };
        toast.info("You don't have access to that page. Sent to your workspace.");
        navigate(data.url || '/', { replace: true });
      } catch {
        navigate('/', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, minTier]);

  if (minTier) {
    if (tierBlocked) return <TierNotice minTier={minTier} user={user} />;
    return <>{children}</>;
  }
  if (!checked) return fallback;
  return <>{children}</>;
}

export { canonicalRole, rankOf };
